import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import ApplyModal from './ApplyModal';

const FALLBACK_MENUS = [
  { id: 1, parent_id: 0, sort_order: 1, title: 'Home', slug: 'home', url: '/', target: '_self' },
  { id: 2, parent_id: 0, sort_order: 2, title: 'About us', slug: 'about', url: '/about-us', target: '_self' },
  { id: 22, parent_id: 2, sort_order: 1, title: 'About Cambridge', slug: 'about-us', url: '/about-us', target: '_self' },
  { id: 23, parent_id: 2, sort_order: 2, title: 'Why us', slug: 'why-us', url: '/why-us', target: '_self' },
  { id: 3, parent_id: 0, sort_order: 3, title: 'Academics', slug: 'academics', url: '/curriculum-and-infrastructure', target: '_self' },
  { id: 301, parent_id: 3, sort_order: 1, title: 'Curriculum and Infrastructure', slug: 'curriculum-and-infrastructure', url: '/curriculum-and-infrastructure', target: '_self' },
  { id: 302, parent_id: 3, sort_order: 2, title: 'Extracurricular Activities', slug: 'extra-curriculum-activities', url: '/extra-curriculum-activities', target: '_self' },
  { id: 303, parent_id: 3, sort_order: 3, title: 'Other Facilities', slug: 'other-facilities', url: '/other-facilities', target: '_self' },
  { id: 304, parent_id: 0, sort_order: 5, title: 'Gallery', slug: 'gallery', url: '/gallery', target: '_self' },
  { id: 4, parent_id: 0, sort_order: 4, title: 'Admission', slug: 'admission', url: '/admission', target: '_self' },
  { id: 5, parent_id: 0, sort_order: 6, title: 'Blog', slug: 'article', url: '/article', target: '_self' },
  { id: 6, parent_id: 0, sort_order: 7, title: 'Contact', slug: 'contact', url: '/contact-us', target: '_self' },
  { id: 61, parent_id: 6, sort_order: 1, title: 'Contact us', slug: 'contact-us', url: '/contact-us', target: '_self' },
  { id: 62, parent_id: 6, sort_order: 2, title: 'Job Application Form', slug: 'job-application-form', url: '/job-application-form', target: '_self' },
];

const SCHOOLS = [
  { name: 'Dehradun', url: 'https://www.Cambridgedehradun.in/' },
  { name: 'Bhadana', url: 'https://cambridgeschooljjr.com/' },
  { name: 'Gurugram', url: 'https://www.Cambridgegurugram.com/' },
  { name: 'Little Cambridge', url: 'https://littleandrews.in/' },
  { name: 'Vasundhara', url: 'https://Cambridgevasundhara.com/' },
  { name: 'Sonipat', url: 'https://www.Cambridgesonipat.com/' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [menus, setMenus] = useState([]);
  const [settings, setSettings] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const schoolRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuRes, settingRes, contactRes] = await Promise.all([
          API.get('/menus/public'),
          API.get('/settings/public'),
          API.get('/contacts/public'),
        ]);
        setMenus(menuRes.data.data || []);
        const s = settingRes.data.data || {};
        const c = contactRes.data.data || {};
        setSettings({ ...s, ...c });
        if (s.favicon) {
          let link = document.querySelector("link[rel~='icon']") || document.querySelector('link[rel="shortcut icon"]');
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = `${UPLOADS_URL}/${s.favicon}`;
        }
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (schoolRef.current && !schoolRef.current.contains(e.target)) setSchoolOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!menuOpen) setOpenSubMenus({});
  }, [menuOpen]);

  useEffect(() => {
    setOpenSubMenus({});
  }, [location.pathname]);

  const isActive = (slug) => {
    const path = location.pathname;
    if (slug === 'home' && path === '/') return true;
    return path === `/${slug}` || path.startsWith(`/${slug}/`);
  };

  const effectiveMenus = menus.length ? menus : FALLBACK_MENUS;
  const topMenus = effectiveMenus.filter(m => m.parent_id === 0).sort((a, b) => a.sort_order - b.sort_order);

  const getChildren = (parentId) =>
    effectiveMenus.filter(m => m.parent_id === parentId).sort((a, b) => a.sort_order - b.sort_order);

  const toggleSubMenu = (id, force) => {
    setOpenSubMenus(prev => ({ ...prev, [id]: force !== undefined ? force : !prev[id] }));
  };

  const renderMenu = (items, depth = 0, parentId = null) => (
    <ul className={
      depth === 0
        ? `flex flex-col lg:flex-row lg:items-center lg:justify-center text-left ${isMobile ? 'bg-white' : ''}`
        : depth === 1
          ? 'lg:absolute lg:left-0 lg:top-full bg-white shadow-lg z-50 lg:min-w-[220px] w-full py-1 sub-menu text-left border-t-2 border-secondary' +
            (depth > 0 && parentId && openSubMenus[parentId] ? ' open' : '')
          : 'lg:absolute lg:left-full lg:top-0 bg-white shadow-lg z-50 lg:min-w-[220px] w-full py-1 sub-menu text-left border-t-2 border-secondary' +
            (depth > 0 && parentId && openSubMenus[parentId] ? ' open' : '')
    }
    >
      {items.map(menu => {
        const children = getChildren(menu.id);
        const hasChildren = children.length > 0;
        return (
          <li key={menu.id} className="relative group"
            onMouseEnter={() => { if (hasChildren) toggleSubMenu(menu.id, true); }}
            onMouseLeave={() => { if (hasChildren) toggleSubMenu(menu.id, false); }}
          >
            <div className="flex items-center justify-between">
              <Link to={menu.url}
                    className={`flex-1 block px-4 py-3 text-[15px] capitalize font-semibold transition-colors
                               ${depth === 0 ? 'lg:px-3 text-primary hover:text-secondary' : 'text-body hover:text-primary hover:bg-light'}
                               ${isActive(menu.slug) ? 'text-secondary' : ''}`}
                    onClick={() => { if (!hasChildren) setMenuOpen(false); }}
                    target={menu.target}>
                {menu.title}
              </Link>
              {hasChildren && (
                <button onClick={(e) => { e.preventDefault(); toggleSubMenu(menu.id); }}
                        className={`px-3 py-2.5 text-body hover:text-primary transition-colors text-xs lg:hidden`}
                        aria-label="Toggle submenu">
                  <i className={`fas fa-chevron-${openSubMenus[menu.id] ? 'down' : 'right'} transition-transform`}></i>
                </button>
              )}
            </div>
            {hasChildren && renderMenu(children, depth + 1, menu.id)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#042D8E] text-white/80 py-2 text-sm relative z-[60]">
        <div className="container flex items-center justify-between">
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:+919034700144" className="hover:text-white transition-colors"><i className="fas fa-phone-alt mr-1"></i> +919034700144</a>
            <a href="mailto:cambridgebhadana@gmail.com" className="hover:text-white transition-colors"><i className="fas fa-envelope mr-1"></i> cambridgebhadana@gmail.com</a>
          </div>
          <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
            <div className="md:hidden flex items-center gap-4">
              <a href="tel:+919034700144" className="text-white/85 hover:text-white transition-colors" aria-label="Call"><i className="fas fa-phone"></i></a>
              <a href="mailto:cambridgebhadana@gmail.com" className="text-white/85 hover:text-white transition-colors" aria-label="Email"><i className="fas fa-envelope"></i></a>
            </div>
            <div className="flex gap-3 items-center">
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
              <a href="https://www.instagram.com/cambridgeschooljjr/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
              <a href="https://youtube.com/channel/UCN-rxHSuiW_R7rPaTuvb5dw" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
            </div>

          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white border-b border-gray-100 py-0 sticky top-0 z-50">
        <div className="container">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <img src={`${process.env.PUBLIC_URL}/images/logo.jpg`} className="h-[4.5rem] md:h-[7rem] w-auto"
                   alt="Cambridge International Sr. Sec. School" />
            </Link>
            
            {/* Navigation for Desktop */}
            <nav className="hidden lg:block relative main-nav flex-1 flex justify-center">
              {renderMenu(topMenus)}
            </nav>

            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <button onClick={() => setApplyOpen(true)} className="btn-primary">
                Apply Now <i className="fas fa-arrow-right ml-2 text-[12px]"></i>
              </button>
            </div>
            
            <button className="lg:hidden bg-primary text-white px-4 py-2 rounded-md focus:outline-none shrink-0" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle Menu">
              <i className={`fas ${menuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 absolute top-full left-0 w-full shadow-lg max-h-[80vh] overflow-y-auto">
            <nav className="relative main-nav">
              {renderMenu(topMenus)}
            </nav>
          </div>
        )}
      </header>

      {menuOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMenuOpen(false)} />}
      <button
        type="button"
        onClick={() => setApplyOpen(true)}
        className="fixed top-1/2 right-0 -translate-y-1/2 z-[60] flex items-center justify-center bg-[#D32F2F] text-white py-[18px] px-[14px] text-[13px] font-bold tracking-[1.5px] uppercase rounded-l-[10px] shadow-[-4px_4px_20px_rgba(211,47,47,0.35)] hover:bg-[#B71C1C] hover:shadow-[-6px_6px_25px_rgba(211,47,47,0.5)] transition-all"
        style={{ writingMode: 'vertical-lr', textOrientation: 'mixed', lineHeight: 1.4 }}
      >
        Enquiry Now
      </button>
      <ApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} />
    </>
  );
};

export default Header;
